import { useEffect } from 'react';
import analytics from '../utils/analytics';

export interface ContentViewedAnalyticsParams {
    /** The type/container of the content being viewed */
    eventContainer: string;
    /** The ID of the content entry */
    entryId: string;
    /** The URL of the content entry */
    entryUrl: string;
    /** The type of parent page (e.g., 'topic-set', 'collection', 'kb-home') */
    parentPageType: string;
    /** The URL of the parent page */
    parentPageUrl: string;
}

/**
 * Hook to track content viewed analytics events for BWAV-2192
 *
 * This hook automatically tracks when content is viewed and can be used
 * across different content types (topics, kb-articles, etc.)
 *
 * The hook automatically includes the following analytics fields via track-event.ts:
 * - content_ari: Contentful ARI of the current page
 * - content_id: Contentful entry ID (spec requirement)
 * - contentful_entry_id: Contentful entry ID (legacy naming)
 * - content_type: Type of content (e.g., 'topic', 'kb-article')
 * - page_canonical_url: Canonical URL of the current page
 * - page_referrer_url: URL of the referring page
 * - is_logged_in: User authentication status
 *
 * Additionally sends from this hook:
 * - eventContainer: The context of the viewed content
 * - event: Always set to 'viewed'
 * - eventComponent: Always set to 'content'
 * - parent_page_type: Type of parent page (e.g., 'topic-set')
 * - parent_page_url: URL of the parent page
 * - view_format: Always set to 'page'
 *
 * @param params - The analytics parameters for tracking content views
 */
export const useContentViewedAnalytics = (
    params: ContentViewedAnalyticsParams
): void => {
    const { eventContainer, entryId, entryUrl, parentPageType, parentPageUrl } =
        params;

    useEffect(() => {
        analytics.trackEvent('content viewed', {
            eventContainer,
            event: 'viewed',
            eventComponent: 'content',
            parent_page_type: parentPageType,
            parent_page_url: parentPageUrl,
            view_format: 'page'
        });
    }, [eventContainer, entryId, entryUrl, parentPageType, parentPageUrl]);
};
